import { Link } from "react-router-dom";
import Meta from "../../partials/meta";
import { contactEmail } from "../../shared";
import styles from "../TermsOfUse/terms.module.scss";

const PrivacyEmail = (): JSX.Element => (
  /*TEMPORARILY DISABLED UNTIL SET UP BY I.T. <a href={`mailto:privacy@motional.com`}>privacy@motional.com</a>*/
  <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
);

export const PrivacyPolicy = (): JSX.Element => (
  <div className={styles.bg}>
    <Meta
      title="Privacy Policy"
      description="The privacy policy for the nuScenes, nuImages, nuPlan, and nuReality datasets."
    />
    <div className="content text-justify">
      <div className={styles.content}>
        <h1>Privacy Policy</h1>
        <p>This Privacy Policy was last updated on Nov 16, 2021</p>
        <h1>Introduction</h1>
        <p>
          Motional AD Inc. and its affiliates (“Motional,” “we, ” “us, ” “our”)
          have created this Privacy Policy in compliance with applicable law and
          in order to demonstrate our company’s commitment to fairly and
          transparently handling your personal information or personal data.
          This Privacy Policy outlines the types of personal information
          Motional may collect from you or that you may provide to us when you
          use our websites, www.nuScenes.org or www.nuReality.org (the
          “Websites”) and our practices for collecting, using, maintaining,
          protecting, and disclosing that information. This Privacy Policy does
          not apply to information collected by:
          <p>
            <li className={styles.list1}>
              Us offline or through any other means, including on any other
              website operated by Motional; or{" "}
            </li>
            <li className={styles.list1}>
              Any third party, including through any application or content that
              may link to or be accessible from or on the Websites
            </li>
          </p>
          <p>
            Please read this Privacy Policy carefully to understand our
            practices regarding your information and how we will treat it. If
            you do not agree with our policy and practices, you should choose
            not to use the Websites. By accessing or using the Website, you
            agree to this Privacy Policy.
          </p>
        </p>
        <h1>General Information</h1>
        <p>
          This Privacy Policy applies to personal information collected when you
          use the Websites and any Motional datasets available on the Websites
          including but not limited to nuScenes, nuImages, nuPlan, and nuReality
          (“Datasets”). Visitors to the Websites in the European Economic Area
          (“EEA”) and the United Kingdom should review our EEA and UK Privacy
          Policy Provisions. Our Websites may contain links to other,
          third-party, websites that we do not operate. If you click on a
          third-party link and visit another website we do not operate, we
          encourage you to review the privacy policy of the other website.
        </p>
        <h1>Children’s Privacy</h1>
        <p>
          Our Websites are not intended for individuals under the age of
          eighteen (18) years old. We do not knowingly maintain or collect
          personal information relating to any person under the age of 18
          through our Websites. If you are under the age of 18, you should not
          use the Websites or supply any personal information through the
          Websites. If you are under the age of eighteen (18) years old and have
          already provided personal information through the Websites, please
          have your parent or guardian contact us at <PrivacyEmail /> so that we
          can delete information relating to you.
        </p>
        <h1>Collection and Use of Personal Information</h1>
        <p>
          We take your privacy and the protection of your personal information
          seriously. We will only store, process, and disclose your personal
          information where we have your consent or a legitimate interest and in
          accordance with applicable law. We may collect, store and use personal
          information for typical business management, operational, and
          commercial purposes (including to defend and protect us and others
          from harm or legal claims and as required by applicable law), and as
          described more specifically below. In connection with the Websites, we
          may collect and use personal information for the following purposes:
        </p>

        <h2>Identifiers</h2>
        <p>
          We receive personal identifiers (such as your name, email address, and
          telephone number) when you fill out a form or contact us through the
          Websites, and we use them to respond to your requests and to
          communicate with you for informational and marketing purposes.
        </p>
        <h2>Communications</h2>
        <p>
          We receive information you provide to us through your communications
          with us by email, posts to social media pages, or another method. We
          process this information to respond to you and in the normal course of
          our business operations. For example, we may receive the following
          personal information from you - personal details included in your
          communications to us about the Datasets, professional or
          business-related information (such as your job title and company
          name), and other message content.
        </p>
        <h2>Data collection</h2>
        <p>
          We collect information about your computer or mobile device and your
          visits to the Websites, including identifiers (such as the relevant IP
          address(es)), information about your device (such as estimated
          geographical location and browser type), and internet or other
          electronic activity information (such as interactions with the
          Websites including, browsing history, search history, Websites usage
          information). We may use this information to administer and improve
          the Websites, to improve our services, for security purposes, and to
          tailor our marketing and other communications to you. In some cases,
          our third-party partners may process information collected by cookies
          and related technologies that we permit them to use on the Websites.
          Please see our Cookie Policy for more details. With respect to any
          social media pages that we may use, please see the privacy policies
          and any cookie policies of the applicable social media providers as to
          their automated data collection practices.
        </p>
        <h2>Inferences</h2>
        <p>
          We may draw inferences from the categories of information described
          above. This information may be used for the purposes described as to
          such categories above.
        </p>
        <h1>Retention of Information</h1>
        <p>
          We utilize, disclose, or retain your personal information collected in
          connection with the Websites for as long as necessary to fulfill the
          purposes for which it was collected and for legal or business
          requirements. We adhere to established retention periods and
          procedures for maintaining and destroying your personal information.
        </p>
        <h1>Protection of Information</h1>
        <p>
          We implement security measures designed to secure your personal
          information from accidental loss and from unauthorized access, use,
          alteration, and disclosure. We also implement various safeguards
          including, but not limited to, encryption, access limitations, and
          authentication to help guard against unauthorized access to personal
          information in our systems. Please remember, though, that our ability
          to implement safeguards to protect internet communications is limited.
        </p>
        <h1>Sharing of Personal Information</h1>
        <p>
          We may share personal information we receive for our business and
          commercial purposes and as more specifically described below:
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              Within our corporate family of parents, subsidiaries, affiliates,
              and joint ventures in order to efficiently carry out our business
              to the extent permitted by law.
            </li>
            <li className={styles.list1}>
              To service providers (such as IT service vendors, website hosting
              facilities, and email distribution services), contractors, and
              other third-parties we use to support our business. When we
              disclose personal information for a business purpose, we take
              reasonable steps to help ensure the recipient uses the personal
              information for the purpose of performing the contract and
              protects the personal information.
            </li>
            <li className={styles.list1}>
              In order to comply with an applicable court order, law, or legal
              process, which includes responding to relevant government or
              regulatory requests.
            </li>
            <li className={styles.list1}>
              To enforce our Terms of Use above or any other agreements we have
              with you.
            </li>
            <li className={styles.list1}>
              If we believe disclosure is necessary or appropriate to detect,
              investigate, prevent, or take action against illegal activities,
              fraud, or situations regarding the safety or rights of Motional,
              our employees, you, or others.
            </li>
            <li className={styles.list1}>
              In the event of a merger, acquisition, financing due diligence,
              reorganization, bankruptcy, receivership, sale of company assets,
              or transition of service to another business unit (whether by
              private sale, through operation of law, as part of a divestiture
              plan, or otherwise), to the persons and/or entities participating
              in the transaction diligence or assuming control of the assets,
              our advisors, and as otherwise necessary to complete the
              transaction as permitted by law or contract.
            </li>
            <li className={styles.list1}>
              To any other third-party disclosed to you when you provide the
              personal information.
            </li>
            <li className={styles.list1}>
              With your consent at your direction.
            </li>
          </ul>
        </p>
        <p>
          Generally, Motional may share aggregated or anonymized information -
          information that does not identify an individual or household.
        </p>
        <h1>Data Subject Rights</h1>
        <p>
          Certain jurisdictions impose specific legal requirements and privacy
          rights with respect to personal information, and we will comply with
          restrictions and any requests you submit as required by applicable
          law. For example, you may have the right to review, correct, and
          delete personal information we have about you or to consent or
          withdraw consent to certain uses or sharing of personal information.
          If you find personal information - including your personal information
          - in our Motionalverse datasets, you can request its removal by
          contacting us and including: a reference to the frame or video that
          you would like removed or de-identified and an explanation of why the
          information in the Motionalverse dataset needs to be removed or
          de-identified. If you would like to request access to such information
          or that we update, correct, or delete any such information, please
          send an email to us at: <PrivacyEmail /> . Note that, in handling any
          such request, we will ask you to provide us with sufficient
          information to confirm your identity.
        </p>
        <h2>Rights of California Consumers</h2>
        <p>
          This section applies to the personal information of California
          consumers, in keeping with the California Consumer Privacy Act
          (“CCPA”) and other California law.
        </p>
        <p>
          The CCPA provides California consumers with specific rights regarding
          their personal information. They can request:
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              additional information about our data collection, use, disclosure,
              and sales practices in connection with the consumer’s personal
              information;
            </li>
            <li className={styles.list1}>
              the specific personal information collected about the consumer
              during the previous 12 months; and
            </li>
            <li className={styles.list1}>
              deletion of the personal information we have about the consumer.
            </li>
          </ul>
        </p>
        <p>
          Please note that we do not apply any facial recognition or other
          personally identifying technology to the images and other data in the
          Datasets, and thus, we cannot provide or delete any such information
          in response to a data subject request. To make a request to us
          regarding your personal information, you may use the form at the{" "}
          <Link to="/privacy-takedown">Takedown Request</Link> page.
        </p>
        <p>
          Under the CCPA, California consumers may use an authorized agent to
          make privacy rights requests. We require any such authorized agent to
          provide us with proof of the California consumer’s written permission
          (for example, a power of attorney) that demonstrates authorization to
          submit a request for the California consumer. An authorized agent must
          follow the process described above to make a request, and we will also
          (a) require the authorized agent to verify the agent’s own identity
          and prove that the agent is registered to do business in California,
          and (b) confirm the agent’s authority with the California consumer
          about whom the request was made.
        </p>
        <p>
          We endeavor to respond to a verified California consumer request
          within forty-five days of its receipt. We may request additional
          information in order to sufficiently respond to your request, and if
          we require more time, we will let you know.
        </p>
        <p>
          We do not charge a fee to process and respond to your verified
          consumer request unless it is excessive, repetitive, or unfounded. If
          there is a fee associated with your request, we will inform you of the
          decision and the cost estimate before completing your request.
        </p>
        <p>
          We do not sell personal information, and we have not sold personal
          information since January 1, 2020, the date the CCPA went into effect.
          We also do not rent, sell, or share personal information (as defined
          by California Civil Code § 1793.83) about you that we collect with
          other people or unaffiliated companies for their direct marketing
          purposes, unless we have your permission.
        </p>
        <p>
          We will not discriminate against you for exercising any of your
          privacy rights, as required by applicable law. As required by
          applicable law, we will not deny you services, charge you differently,
          or provide you with a different quality of service.
        </p>

        <h1>International Transfers</h1>
        <p>
          These Websites are hosted in the United States. By using our Websites,
          you understand that your information will be transferred to our data
          center and servers in the United States and potentially to third
          parties with whom we may share your personal information as described
          in this Privacy Policy. The data protection and other laws of the
          United States and other countries might not be as comprehensive as
          those in your country of residence. If you are visiting this Websites
          from outside the United States and do not wish to allow the transfer
          of your personal information to the United States, you should not use
          these Websites and opt-out of the collection of cookies.
        </p>

        <h1>Contact</h1>
        <p>
          Personal information is generally located at our corporate offices in
          the United States. Please direct all complaints or other inquiries
          regarding personal information or this Privacy Policy to our privacy
          officer as follows:
        </p>
        <p>
          Email: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </p>
        <p>
          Address: <br />
          Motional AD Inc. <br />
          ATTN: nuScenes Team <br />
          100 Northern Avenue <br />
          Boston, MA 02210
        </p>
        <h1>EEA and UK Privacy Policy Provisions</h1>
        <p>
          The following information and rights only apply to personal data
          associated with individuals who visit the Websites from the European
          Economic Area (“EEA”) and the United Kingdom (“UK”).
        </p>
        <h2>Data Controller</h2>
        <p>
          For the purposes of data collection in connection with the Websites,
          the data controller is Motional, LLC, the address and email address of
          which may be contacted as set forth in the above “Contact” section. If
          you have any questions in connection with the Websites about how your
          personal data is processed or would like to exercise rights as a data
          subject, you may also reach out to our Data Protection Officer at{" "}
          <PrivacyEmail />
        </p>
        <h2>Purpose and Basis for Processing</h2>
        <p>We may collect and use personal data for the following purposes:</p>
        <p>
          In connection with the Websites, we may collect and use personal data
          for the following purposes:
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              We receive personal identifiers (such as your name, email address,
              and telephone number) when you fill out a form or contact us
              through the Websites, and we use them to respond to your requests
              and to communicate with you for informational purposes (based on
              our legitimate interest according to Art. 6 I (f) GDPR). We
              further process such data for marketing purposes in case you have
              explicitly provided your consent (according to Art. 6 I (a) GDPR).
            </li>
            <li className={styles.list1}>
              We receive data you provide to us through your communications with
              us by email, posts to social media pages, or other methods. We
              process this data to respond to you and in the normal course of
              our business operations (based on our legitimate interest
              according to Art. 6 I (f) GDPR). For example, we may receive the
              following personal data from you – personal details included in
              your communications, professional or business-related data (such
              as your job title and company name), and other message content.
            </li>
            <li className={styles.list1}>
              We collect data about your computer or mobile device and your
              visits to the Websites, including identifiers (such as the
              relevant IP address(es)), data about your device (such as
              estimated geographical location and browser type), and internet or
              other electronic activity data (such as interactions with the
              Websites including, browsing history, search history, Websites
              usage data, etc.). We may use this data to administer and improve
              the Websites, to improve our services, and for security purposes
              (based on our legitimate interest according to Art. 6 I (f) GDPR).
            </li>
            <li className={styles.list1}>
              We may, if we have obtained your consent, use this data to tailor
              our marketing and other communications to you. In some cases, our
              third-party partners may process data collected by cookies and
              related technologies that we permit them to use on the Websites.
              Please see our Cookie Policy below for more details. With respect
              to any social media pages that we may use, please see the privacy
              policies and any cookie policies of the applicable social media
              providers as to their automated data collection practices.
            </li>
          </ul>
        </p>

        <h2>Data Sharing</h2>
        <p>We may share personal data we receive for the following purposes:</p>
        <p>
          <ul>
            <li className={styles.list1}>
              With our local affiliates to which a request we receive from you
              relates. In this case, this notice shall also apply to the
              processing of your data by that entity (based on fulfilling your
              request according to Art. 6 I (b)).
            </li>
            <li className={styles.list1}>
              To service providers (such as IT service vendors, website hosting
              facilities, and email distribution services), contractors, and
              other third-parties we use to support our business. Such vendors
              are contractually bound to us and operate under data processing
              agreements (based on our legitimate interest according to Art. 6 I
              (f) GDPR).
            </li>
            <li className={styles.list1}>
              To courts or authorities or other third parties in order to comply
              with an applicable court order, law, or legal process, which
              includes responding to relevant government or regulatory requests
              (based on our legitimate interest according to Art. 6 I (c) GDPR).
            </li>
            <li className={styles.list1}>
              To courts or authorities or other third parties in order to
              enforce our Terms of Use or any other agreements we have with you
              (based on our legitimate interest according to Art. 6 I (f) GDPR).
            </li>
            <li className={styles.list1}>
              To courts or authorities or other third parties if we believe
              disclosure is necessary or appropriate to detect, investigate,
              prevent, or take action against illegal activities, fraud, or
              situations regarding the safety or rights of Motional, our
              employees, you, or others (based on our legitimate interest
              according to Art. 6 I (f) GDPR).
            </li>
            <li className={styles.list1}>
              In the event of a merger, acquisition, financing due diligence,
              reorganization, bankruptcy, receivership, to the persons and/or
              entities participating in the transaction diligence, assuming
              control of the assets, and our advisors (based on our legitimate
              interest according to Art. 6 I (f) GDPR).
            </li>
            <li className={styles.list1}>
              With your prior consent (based on Art. 6 I (a) GDPR).
            </li>
          </ul>
        </p>

        <h2>International Transfers of EEA or UK data</h2>
        <p>
          Your data will be processed and stored within the EEA and United
          States – unless otherwise stated in this Privacy Policy. In the case
          of transfer to third countries which do not provide for an adequate
          level of data protection, we apply additional safeguards in accordance
          with applicable data protection regulations to ensure that the
          recipient adheres to the principles of data protection applicable in
          the EEA / the UK. You may request further information about the
          implemented safeguards by reaching out to us under the above contact
          details.
        </p>

        <h1>How Long We Process Personal Data</h1>
        <p>
          As a general matter, we will delete or anonymize your personal data
          when it is no longer necessary to process it for the purpose(s) for
          which it was collected. When personal data needs to be retained, we
          apply appropriate security controls to prevent the unauthorized
          accessing, use, or processing of the data and take appropriate
          measures to prevent accidental loss, destruction, or damage to the
          data. We conduct periodic reviews to ensure that personal data is
          securely deleted consistent with our policies and applicable law.
        </p>
        <p>
          Note that we may retain the personal data for a prolonged period of
          time for archiving purposes in the public interest, scientific or
          historical research purposes, or statistical purposes, including if
          retention of the personal data is necessary to fulfil legal
          obligations (including under legislation or regulations, to defend or
          assert legal claims, for fraud prevention, or for litigation hold
          purposes).
        </p>

        <h1>Your Rights as a EEA or UK Data Subject</h1>
        <p>
          As a data subject in the EEA or the UK you have certain rights in
          relation to our processing of your personal data in accordance with
          applicable data protection regulations:
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              Right to access (Art. 15 GDPR), right to data portability (Art. 20
              GDPR), Right to rectification (Art. 16 GDPR), right to erasure
              (Art. 17 GDPR), right to restriction (Art. 18 GDPR)
            </li>
            <li className={styles.list1}>
              Right to object. In certain circumstances (e.g., where the legal
              basis for processing is a legitimate interest or if we are
              performing direct marketing), you may object to continued
              processing for those purposes. If you do, we will re-evaluate our
              legitimate business interests and your data protection interests.
              We will only continue the processing activity if (i) there are
              compelling legitimate grounds for the processing that override
              your interests, rights and freedoms, or (ii) the processing is
              necessary to establish, enforce or defend legal claims.
            </li>
            <li className={styles.list1}>
              Right to lodge a complaint. We prefer to resolve any data
              protection concerns directly with you. However, you have the right
              to submit a complaint with a competent supervisory authority, e.g.
              in the EEA country (or the UK) where you reside, work, or suspect
              an infringement has occurred.
            </li>
          </ul>
        </p>
        <p>
          If you would like to assert your rights as a data subject or have
          questions concerning our processing activities, please contact us as
          set forth in the “Data Controller” section above. For additional
          information from the European Commission concerning data protection
          and your rights, see:{" "}
          <a
            href="https://ec.europa.eu/info/law/law-topic/data-protection/reform/rights-citizens_en"
            target="_blank"
            rel="noreferrer"
          >
            https://ec.europa.eu/info/law/law-topic/data-protection/reform/rights-citizens_en
          </a>
          .
        </p>

        <h1>Cookie Policy</h1>
        <p>
          Our Websites use cookies for technical, analytical, and marketing
          purposes.
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              What are cookies and why do we use them? Cookies are files that
              are downloaded on your computer or mobile device when you visit
              certain websites. Cookies allow a website, for instance, to
              identify a user session, recall your preferences (e.g. language),
              or gather information about your browsing habits. We use essential
              cookies (e.g., crumb, recent redirect, and test) to facilitate
              your access to certain features of the Websites such as assisting
              you in navigation on the Websites, improve the functionality of
              the Websites, store login details for our Websites, perform system
              administration, and implement security features.
            </li>
            <li className={styles.list1}>
              How to restrict cookies. You may be able to adjust the settings in
              your browser in order to restrict or block cookies that are set by
              the services or information you access on our Websites (or any
              other website on the internet). Your browser may include
              information on how to adjust your settings. Some internet browsers
              include the ability to transmit “Do Not Track” signals. The
              Websites does not process or respond to such “Do Not Track”
              signals for functional, required, and consented to cookies.
              Alternatively, you may visit www.allaboutcookies.org to obtain
              comprehensive general information about cookies and how to adjust
              the cookie settings on various browsers.
            </li>
            <li className={styles.list1}>
              Please be aware that restricting some cookies may impact the
              functionality of the Websites.
            </li>
          </ul>
        </p>
        <p>
          Upon your consent, we may use third-party advertising services to
          serve advertisements on our behalf when you visit our Websites and
          other websites and third-party analytics services to help us
          understand and improve the usage of our Websites and the effectiveness
          of our marketing efforts. In some situations, we permit the companies
          providing these services to set and access cookies (and use similar
          data tools, such as “pixel tags” or “web beacons”) through our
          Websites. These third-party companies also may collect and combine
          information about your online activities over time, on other devices,
          and on other websites or online services, if those websites and online
          services also use the same companies. Information about cookies and
          similar technology placed by third-party companies on our Websites and
          their related data practices is set forth in the Cookie List below.
        </p>

        <h1>Cookie List</h1>
        <p>
          A cookie is a small piece of data (text file) that a website – when
          visited by a user – asks your browser to store on your device in order
          to remember information about you, such as your language preference or
          login information. Those cookies are set by us and called first-party
          cookies. We also use third-party cookies – which are cookies from a
          domain different from the domain of the website you are visiting – for
          our advertising and marketing efforts. More specifically, we use
          cookies and other tracking technologies for the following purposes:
        </p>
        <h2>Functional Cookies</h2>
        <p>
          These cookies enable the website to provide enhanced functionality and
          personalisation. They may be set by us or by third party providers
          whose services we have added to our pages. If you do not allow these
          cookies then some or all of these services may not function properly.
          We do not use Functional Cookies.
        </p>
        <h2>Strictly Necessary Cookies</h2>
        <p>
          These cookies are necessary for the website to function and cannot be
          switched off in our systems. They are usually only set in response to
          actions made by you which amount to a request for services, such as
          setting your privacy preferences, logging in or filling in forms. You
          can set your browser to block or alert you about these cookies, but
          some parts of the site will not then work. These cookies do not store
          any personally identifiable information. We do not use Strictly
          Necessary Cookies.
        </p>
        <h2>Targeting Cookies</h2>
        <p>
          These cookies may be set through our site by our advertising partners.
          They may be used by those companies to build a profile of your
          interests and show you relevant adverts on other sites. They do not
          store directly personal information, but are based on uniquely
          identifying your browser and internet device. If you do not allow
          these cookies, you will experience less targeted advertising. Our
          Websites may host cookies from third parties such as Google Analytics,
          Youtube and Scale.Ai. However, we are not responsible for any
          information collected by these third-party cookies.
        </p>
        <h2>Performance Cookies</h2>
        <p>
          These cookies allow us to count visits and traffic sources so we can
          measure and improve the performance of our site. They help us to know
          which pages are the most and least popular and see how visitors move
          around the site. All information these cookies collect is aggregated
          and therefore anonymous. If you do not allow these cookies we will not
          know when you have visited our site, and will not be able to monitor
          its performance.
        </p>
        <p>
          Like many websites, the Websites uses Google Analytics to collect
          information about how users use the Websites. This information is
          transferred to servers of Google in the US. We have activated the IP
          Anonymization feature to ensure that your IP address is truncated
          prior to transfer to the US. Google will process your data in order to
          provide us with aggregated overviews for assessing the use of our
          website to help us improve our website and offers. Google will not
          match the data with other data it may store about you. You may be able
          to opt-out from being tracked by Google Analytics in the future by
          downloading and installing ‘Google Analytics Opt-out Browser Add-on’
          for your current web browser:{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noreferrer"
          >
            https://tools.google.com/dlpage/gaoptout
          </a>
          . In addition, you can visit{" "}
          <a
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noreferrer"
          >
            https://policies.google.com/technologies/partner-sites
          </a>{" "}
          to find out more information about your options with respect to Google
          Analytics and other Google services.
        </p>
        <p>
          You may learn more about advertising networks and opt out of receiving
          personalized advertisements on your browser or device from advertisers
          who are members of the Network Advertising Initiative or who subscribe
          to the Digital Advertising Alliance’s Self-Regulatory Principles for
          Online Behavioral Advertising by visiting the opt-out options of each
          of those organizations. Links to those websites are here:
        </p>
        <p>
          <ul>
            <li className={styles.list1}>
              <a
                href="http://www.networkadvertising.org/choices/"
                target="_blank"
                rel="noreferrer"
              >
                Network Advertising Initiative
              </a>
            </li>
            <li className={styles.list1}>
              <a
                href="http://www.aboutads.info/choices/"
                target="_blank"
                rel="noreferrer"
              >
                Digital Advertising Alliance
              </a>
            </li>
          </ul>
        </p>
        <p>
          Note that, when you opt out of personalized advertising, you may
          continue to see online advertising on the Websites and our ads on
          other websites and online services but this will not be based on
          personalized advertising.
        </p>

        <h1>Changes to this Privacy Policy and Content on this Page</h1>
        <p>
          We make updates to the Privacy Policy on this page, and we will list
          the date of the last revision as identified at the top of the page.
          You are responsible for ensuring we have a current and active email
          address for you. If we make any material change to the content on this
          page regarding use or disclosure of personal information or data, we
          will provide advance notice on the Websites. Changes will apply to
          data collected after the change is effective.
        </p>
        <h1>User Support</h1>
        <p>
          User support inquiries for our Websites that are unrelated to requests
          regarding personal information described in the Data Subject Rights
          section above are handled through Github Issues. In order to submit a
          request for support or otherwise contact us you would need to create a
          GitHub account. Please note that Github will share with us your GitHub
          account information and that your query will be publicly available on
          GitHub. The information you provide to Github is also governed by
          Github's Privacy Policy. We use this information to answer your
          queries.
        </p>
      </div>
    </div>
  </div>
);
export default PrivacyPolicy;
